import { useState, useEffect } from "react";
import axios from "axios";
import { API } from "@/App";
import { toast } from "sonner";
import DashboardLayout from "@/components/layout/DashboardLayout";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { CreditCard, Building2, Plus, Trash2, CheckCircle, AlertTriangle } from "lucide-react";

const PaymentMethodsPage = () => {
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [selectedMethod, setSelectedMethod] = useState(null);
  const [saving, setSaving] = useState(false);

  const [formData, setFormData] = useState({
    type: "card",
    card_number: "",
    expiry: "",
    cvv: "",
    routing_number: "",
    account_number: ""
  });

  useEffect(() => {
    fetchPaymentMethods();
  }, []);

  const fetchPaymentMethods = async () => {
    try {
      const response = await axios.get(`${API}/payment-methods`, {
        withCredentials: true
      });
      setPaymentMethods(response.data.payment_methods || []);
    } catch (error) {
      console.error("Fetch error:", error);
      toast.error("Failed to load payment methods");
    } finally {
      setLoading(false);
    }
  };

  const handleAddPaymentMethod = async () => {
    setSaving(true);
    try {
      await axios.post(
        `${API}/payment-methods`,
        formData,
        { withCredentials: true }
      );
      toast.success("Payment method added (MOCKED)");
      setShowAddDialog(false);
      resetForm();
      fetchPaymentMethods();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to add payment method");
    } finally {
      setSaving(false);
    }
  };

  const handleDeletePaymentMethod = async () => {
    try {
      await axios.delete(
        `${API}/payment-methods/${selectedMethod.payment_method_id}`,
        { withCredentials: true }
      );
      toast.success("Payment method removed");
      setShowDeleteDialog(false);
      setSelectedMethod(null);
      fetchPaymentMethods();
    } catch (error) {
      toast.error("Failed to remove payment method");
    }
  };

  const resetForm = () => {
    setFormData({
      type: "card",
      card_number: "",
      expiry: "",
      cvv: "",
      routing_number: "",
      account_number: ""
    });
  };

  const formatCardNumber = (value) => {
    const v = value.replace(/\s+/g, "").replace(/[^0-9]/gi, "");
    const matches = v.match(/\d{4,16}/g);
    const match = (matches && matches[0]) || "";
    const parts = [];
    for (let i = 0, len = match.length; i < len; i += 4) {
      parts.push(match.substring(i, i + 4));
    }
    return parts.length ? parts.join(" ") : value;
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="space-y-6">
          <div className="h-8 w-48 skeleton rounded" />
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {[1, 2].map((i) => (
              <div key={i} className="h-32 skeleton rounded" />
            ))}
          </div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6 max-w-3xl" data-testid="payment-methods-page">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold">Payment Methods</h1>
            <p className="text-muted-foreground mt-1">
              Manage your payment methods for subscriptions and investments
            </p>
          </div>
          <Button className="btn-gold" onClick={() => setShowAddDialog(true)}>
            <Plus className="w-4 h-4 mr-2" />
            Add Method
          </Button>
        </div>

        {/* Mocked Payment Notice */}
        <Card className="surface-card border-amber-500/30">
          <CardContent className="p-4">
            <div className="flex items-start gap-3">
              <AlertTriangle className="w-5 h-5 text-amber-500 shrink-0 mt-0.5" />
              <div>
                <p className="font-semibold text-amber-500">Payment Integration (MOCKED)</p>
                <p className="text-sm text-muted-foreground">
                  Payment methods are stored locally for demonstration. Hyfin integration pending.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Payment Methods List */}
        {paymentMethods.length === 0 ? (
          <Card className="surface-card">
            <CardContent className="py-16 text-center">
              <CreditCard className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
              <h3 className="text-lg font-semibold mb-2">No Payment Methods</h3>
              <p className="text-muted-foreground mb-4">
                Add a payment method to enable subscriptions and investments.
              </p>
              <Button onClick={() => setShowAddDialog(true)}>
                <Plus className="w-4 h-4 mr-2" />
                Add Payment Method
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {paymentMethods.map((method) => (
              <Card key={method.payment_method_id} className="surface-card">
                <CardContent className="p-6">
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 rounded-lg bg-secondary flex items-center justify-center">
                        {method.type === "card" ? (
                          <CreditCard className="w-6 h-6 text-primary" />
                        ) : (
                          <Building2 className="w-6 h-6 text-blue-400" />
                        )}
                      </div>
                      <div>
                        <p className="font-semibold">{method.brand || "Card"}</p>
                        <p className="text-muted-foreground">•••• {method.last_four}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      {method.is_default && (
                        <Badge className="bg-primary/20 text-primary">Default</Badge>
                      )}
                      <Button
                        variant="ghost"
                        size="icon"
                        className="text-destructive hover:text-destructive"
                        onClick={() => {
                          setSelectedMethod(method);
                          setShowDeleteDialog(true);
                        }}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>

      {/* Add Payment Method Dialog */}
      <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
        <DialogContent className="bg-card border-border">
          <DialogHeader>
            <DialogTitle>Add Payment Method</DialogTitle>
            <DialogDescription>
              Add a new payment method (MOCKED - no real charges)
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-6 py-4">
            {/* Payment Type */}
            <RadioGroup
              value={formData.type}
              onValueChange={(value) => setFormData({ ...formData, type: value })}
              className="grid grid-cols-2 gap-4"
            >
              <div>
                <RadioGroupItem value="card" id="card" className="peer sr-only" />
                <Label
                  htmlFor="card"
                  className="flex items-center gap-3 p-4 rounded-lg border-2 border-border cursor-pointer peer-data-[state=checked]:border-primary"
                >
                  <CreditCard className="w-5 h-5" />
                  <span>Credit Card</span>
                </Label>
              </div>
              <div>
                <RadioGroupItem value="ach" id="ach" className="peer sr-only" />
                <Label
                  htmlFor="ach"
                  className="flex items-center gap-3 p-4 rounded-lg border-2 border-border cursor-pointer peer-data-[state=checked]:border-primary"
                >
                  <Building2 className="w-5 h-5" />
                  <span>Bank Account</span>
                </Label>
              </div>
            </RadioGroup>

            {/* Card Fields */}
            {formData.type === "card" ? (
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label>Card Number</Label>
                  <Input
                    placeholder="4242 4242 4242 4242"
                    value={formData.card_number}
                    onChange={(e) => setFormData({
                      ...formData,
                      card_number: formatCardNumber(e.target.value)
                    })}
                    maxLength={19}
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Expiry</Label>
                    <Input
                      placeholder="MM/YY"
                      value={formData.expiry}
                      onChange={(e) => setFormData({ ...formData, expiry: e.target.value })}
                      maxLength={5}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>CVV</Label>
                    <Input
                      placeholder="123"
                      type="password"
                      value={formData.cvv}
                      onChange={(e) => setFormData({ ...formData, cvv: e.target.value })}
                      maxLength={4}
                    />
                  </div>
                </div>
              </div>
            ) : (
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label>Routing Number</Label>
                  <Input
                    placeholder="110000000"
                    value={formData.routing_number}
                    onChange={(e) => setFormData({ ...formData, routing_number: e.target.value })}
                    maxLength={9}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Account Number</Label>
                  <Input
                    placeholder="000123456789"
                    value={formData.account_number}
                    onChange={(e) => setFormData({ ...formData, account_number: e.target.value })}
                  />
                </div>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowAddDialog(false); resetForm(); }}>
              Cancel
            </Button>
            <Button
              className="btn-gold"
              onClick={handleAddPaymentMethod}
              disabled={saving}
            >
              {saving ? "Adding..." : "Add Payment Method"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent className="bg-card border-border">
          <AlertDialogHeader>
            <AlertDialogTitle>Remove Payment Method?</AlertDialogTitle>
            <AlertDialogDescription>
              This will remove the {selectedMethod?.brand} ending in {selectedMethod?.last_four} from your account.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeletePaymentMethod}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Remove
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </DashboardLayout>
  );
};

export default PaymentMethodsPage;
